// src/components/IncomingOrders/OrderMetaCompact.jsx

import clsx from "clsx";
import { getISOWeek } from "date-fns";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CommentIcon from "@mui/icons-material/Comment";
import EmailIcon from "@mui/icons-material/Email";
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import EventIcon from "@mui/icons-material/Event";
import {
  Box,
  Chip,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import DebouncedTextField from "./DebouncedTextField";

const useStyles = makeStyles((theme) => ({
  // === “Compact card” – matcha AddressCompact känsla ===
  rootCompact: {
    background: "rgba(0,0,0,0.035)",
    borderRadius: 6,
    padding: "8px 10px",
    width: "100%",
  },
  titleCompact: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.2,
    color: theme.palette.text.secondary,
    marginBottom: 1,
    paddingTop: 10,
  },
  rowCompact: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "2px 0",
    minHeight: 24,
  },
  icon: {
    fontSize: 18,
    color: theme.palette.text.secondary,
  },
  labelCompact: {
    fontSize: 12,
    fontWeight: 600,
    minWidth: 160,
    display: "flex",
    alignItems: "center",
    gap: 4,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  valueCompact: {
    fontSize: 12,
    color: theme.palette.text.primary,
    flex: 1,
    lineHeight: 1.2,
    minWidth: 0,
    //overflow: "hidden",
    //textOverflow: "ellipsis",
    //whiteSpace: "nowrap",
  },

  // === “Wide” – matcha AddressInfo/Compare känsla (label/value) ===
  rootWide: {
    background: "rgba(0,0,0,0.02)",
    borderRadius: 6,
    overflow: "hidden",
    width: "100%",
  },
  headWide: {
    padding: "8px 10px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    background: "rgba(0,0,0,0.02)",
  },
  titleWide: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.2,
    color: theme.palette.text.secondary,
  },
  table: {
    "& tbody tr:nth-of-type(odd)": {
      backgroundColor: "rgba(0,0,0,.02)",
    },
  },
  labelCell: {
    width: 180,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
    verticalAlign: "top",
    borderBottom: "none",
    padding: "6px 10px",
  },
  valueCell: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    verticalAlign: "top",
    borderBottom: "none",
    padding: "6px 10px",
  },

  // Select – tight padding (regression-fix)
  statusSelect: {
    minWidth: 220,
    "& .MuiSelect-select": {
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 10,
      paddingRight: 28, // plats för ikon
      display: "flex",
      alignItems: "center",
      minHeight: 0,
    },
  },

  dirtyField: {
    "&.MuiTextField-root": {
      backgroundColor: "rgba(255, 193, 7, 0.12)",
      boxShadow: "inset 3px 0 0 #f7bd02",
      transition: "background-color 150ms ease",
    },
  },
}));

const formatDateWithWeek = (value) => {
  if (!value) return "";

  const datePart = value.split("T")[0];
  const [year, month, day] = datePart.split("-").map(Number);

  const d = new Date(year, month - 1, day);

  const week = getISOWeek(d);

  return `${datePart} (vecka ${week})`;
};

const OrderMetaCompact = ({
  order,
  settings = {},
  variant,
  updateField,
  isFieldDirty,
  tableSettings
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const theme = useTheme();

  const [snackOpen, setSnackOpen] = useState(false);


  const emailValue = order?.ordererAddress?.email || order?.email || "";

  const metaRows = useMemo(() => {
    const rows = [];

    const supplierOrderReference = tableSettings?.find(item => item.field === "SUPPLIER_ORDER_REFERENCE");

    if (Object.keys(order).length === 0) {
      return [];
    }

    if (order?.deliveryDateWish) {
      rows.push({
        key: "deliveryDateWish",
        icon: <EventIcon className={classes.icon} />,
        label: intl.formatMessage({
          id: "orderHistory.deliveryDateWish",
          defaultMessage: intl.formatMessage({ id: "incomingOrders.order.requestedDeliveryDate" }),
        }),
        value: formatDateWithWeek(order.deliveryDateWish),
        tooltip: formatDateWithWeek(order.deliveryDateWish),
        enabled: settings.showDeliveryDate !== false,
      });
    }

    rows.push({
      key: "status",
      icon: <CheckCircleIcon className={classes.icon} />,
      label: intl.formatMessage({ id: "orderHistory.status", defaultMessage: intl.formatMessage({ id: "incomingOrders.order.status" }) }),
      value: "__STATUS__",
      enabled: settings.showOrderStatus !== false,
    });

    rows.push({
      key: "costPlaceOrder",
      icon: <BusinessIcon className={classes.icon} />,
      label: intl.formatMessage({ id: "orderHistory.costPlace", defaultMessage: intl.formatMessage({ id: "incomingOrders.order.costCenter" }) }),
      value: order?.costPlaceOrder,
      enabled: settings.showCostCenter !== false,
    });

    rows.push({
      key: "email",
      icon: <EmailIcon className={classes.icon} />,
      label: intl.formatMessage({ id: "orderHistory.email", defaultMessage: "E-post" }),
      value: emailValue,
      enabled: !!settings.showEmail,
    });



    rows.push({
      key: "supplierOrderReference",
      icon: <CommentIcon className={classes.icon} />,
      label:  supplierOrderReference?.label  ? supplierOrderReference.label
        : intl.formatMessage({ id: "orderHistory.supplierOrderReference", defaultMessage: "KL Orderreferens" }),
      //label: intl.formatMessage({ id: "orderHistory.supplierOrderReference", defaultMessage: intl.formatMessage({ id: "incomingOrders.order.ourReference" }) }),
      value: order?.supplierOrderReference,
      enabled: true,
      editable: true,
      alwaysShow: true,
    });

    rows.push({
      key: "comment",
      icon: <CommentIcon className={classes.icon} />,
      label: intl.formatMessage({ id: "orderHistory.comment", defaultMessage: intl.formatMessage({ id: "incomingOrders.order.message" }) }),
      value: order?.comment,
      enabled: !!settings.showComment,
      multiLine: true
    });

    return rows.filter(
      (r) =>
        r.enabled &&
        (r.alwaysShow || r.value === "__STATUS__" || (r.value !== null && r.value !== undefined && r.value !== ""))
    );
  }, [order, settings, intl, classes.icon, emailValue, tableSettings]);

  if (Object.keys(order).length === 0) return null;

  const StatusControl = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, fontSize: "12px", color: theme.palette.text.primary }}>
      {intl.formatMessage({ id: "incomingOrders.orderstatus."+ order.orderStatus })}
    </Box>
  );

  const renderControl = (r) => {
    if (r.key === "status") {
      return (
        StatusControl
      );
    }

    if (r.key === "supplierOrderReference" && r.editable) {
      return (
        <DebouncedTextField
          value={r.value}
          onDebouncedChange={(val) => updateField(r.key, val)}
          forceDirty={isFieldDirty(r.key)}
          dirtyStyle={{
            "&.MuiTextField-root fieldset": {
              backgroundColor: "rgba(255, 193, 7, 0.12)",
              boxShadow: "inset 3px 0 0 #f7bd02",
              transition: "background-color 150ms ease",
            }
          }}
          muiProps={{
            size: "small",
            sx: {
              "& .MuiOutlinedInput-input": { px: 0.25, py: 0 },
              "& .MuiOutlinedInput-notchedOutline": { mx: -0.5, my: -0.25 },
            },
          }}
        />
      );
    }

    const valueNode = (
      <Typography
        className={classes.valueCompact}
        sx={r.multiLine ? { whiteSpace: "pre-line" } : undefined}
      >
        {r.value}
      </Typography>
    );

    return r.tooltip
      ? <Tooltip title={r.tooltip}>{valueNode}</Tooltip>
      : valueNode;
  };

  const StripedWideMetadataRows = ({ metaRows, renderControl }) => {
    const ROW_HEIGHT = 30;
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          /*backgroundImage: (theme) => `
            repeating-linear-gradient(
              to bottom,
              transparent,
              transparent ${ROW_HEIGHT}px,
              red ${theme.palette.action.hover} ${ROW_HEIGHT}px,
              blue ${theme.palette.action.hover} ${ROW_HEIGHT * 2}px
            )`,*/
        }}
      > 
        {metaRows.map((r) => (
          <Box
            key={r.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              alignItems: 'center',
              px: 1,
              py: 0,
              minHeight: ROW_HEIGHT,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {r.icon}
              <Typography className={classes.labelCell} variant="body2" noWrap sx={{ px: 0 }}>
                {r.label}
              </Typography>
            </Box>

            <Box className={classes.valueCell} sx={{ p: 0, minWidth: 0 }}>
              {renderControl(r)}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // ====== RENDER: COMPACT ======
  if (variant === "compact") {
    return (
      <Box className={classes.rootCompact}>
        <Typography className={classes.titleCompact}>
          <FormattedMessage id="orderHistory.orderInformation" defaultMessage="Orderinformation" />
        </Typography>

        {metaRows.map((r) => (
          <Box key={r.key} className={classes.rowCompact}>
            {r.icon}

            <Typography className={classes.labelCompact}>
              {r.label}
              {r.key === "status" ? (
                <span style={{ opacity: 0.7 }}>:</span>
              ) : (
                <span style={{ opacity: 0.7 }}>:</span>
              )}
            </Typography>

            {renderControl(r)}
          </Box>
        ))}

        {order?.isPartial && (
          <Box sx={{ mt: 0.75 }}>
            <Tooltip title={intl.formatMessage({ id: "incomingOrders.order.partOfLargerOrder" })}>
              <Chip size="small" label="Delad order" color="warning" variant="outlined" />
            </Tooltip>
          </Box>
        )}

        <Snackbar
          open={snackOpen}
          autoHideDuration={2500}
          message={intl.formatMessage({ id: "incomingOrders.order.statusUpdated" })}
          onClose={() => setSnackOpen(false)}
        />
      </Box>
    );
  }

  // ====== RENDER: WIDE ======
  return (
    <Box className={classes.rootWide}>
      <Box className={classes.headWide}>
        <Typography className={classes.titleWide}>
          <FormattedMessage id="orderHistory.orderInformation" defaultMessage="Orderinformation" />
        </Typography>
      </Box>

      {/*<CompactWideMetadataRows metaRows={metaRows} renderControl={renderControl} />*/}
      <StripedWideMetadataRows metaRows={metaRows} renderControl={renderControl} />

      {order?.isPartial && (
        <Box sx={{ p: "8px 10px" }}>
          <Tooltip title={intl.formatMessage({ id: "incomingOrders.order.partOfLargerOrder" })}>
            <Chip size="small" label="Delad order" color="warning" variant="outlined" />
          </Tooltip>
        </Box>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={2500}
        message={intl.formatMessage({ id: "incomingOrders.order.statusUpdated" })}
        onClose={() => setSnackOpen(false)}
      />
    </Box>
  );
};

OrderMetaCompact.propTypes = {
  order: PropTypes.object.isRequired,
  settings: PropTypes.object,
  variant: PropTypes.oneOf(["compact", "wide"]),
  updateField: PropTypes.func.isRequired,
  isFieldDirty: PropTypes.func.isRequired,
};

OrderMetaCompact.defaultProps = {
  settings: {},
  variant: "compact",
};

OrderMetaCompact.displayName = "OrderMetaCompact";

export default OrderMetaCompact;
